<!-- SIM卡统计页面 - 显示SIM卡概览、状态分布、流量统计等信息 -->
<template>
  <div class="sim-card-stats">
    <!-- 统计概览 -->
    <div class="stats-overview" v-loading="loading">
      <el-row :gutter="20">
        <!-- SIM卡功能状态 -->
        <el-col :span="8">
          <el-card class="stats-card">
            <template #header>
              <div class="card-header">
                <span>SIM卡功能状态</span>
                <el-tag type="info"
                  >总数: {{ statsData.totalCards || 0 }}</el-tag
                >
              </div>
            </template>
            <div class="chart-container">
              <div ref="functionStatusChart" class="chart"></div>
            </div>
            <div class="chart-legend">
              <div
                class="legend-item"
                v-for="item in functionStatusData"
                :key="item.name"
              >
                <span
                  class="legend-color"
                  :style="{ backgroundColor: item.color }"
                ></span>
                <span class="legend-text"
                  >{{ item.name }} {{ item.value }}张</span
                >
              </div>
            </div>
          </el-card>
        </el-col>

        <!-- 即将到期卡 -->
        <el-col :span="8">
          <el-card class="stats-card">
            <template #header>
              <div class="card-header">
                <span>即将到期卡</span>
                <el-tag type="warning"
                  >三个月内: {{ statsData.expiringCards || 0 }}</el-tag
                >
              </div>
            </template>
            <div class="chart-container">
              <div ref="expiringChart" class="chart"></div>
            </div>
            <div class="chart-legend">
              <div
                class="legend-item"
                v-for="item in expiringData"
                :key="item.name"
              >
                <span
                  class="legend-color"
                  :style="{ backgroundColor: item.color }"
                ></span>
                <span class="legend-text"
                  >{{ item.name }} {{ item.value }}张</span
                >
              </div>
            </div>
          </el-card>
        </el-col>

        <!-- SIM卡业务状态 -->
        <el-col :span="8">
          <el-card class="stats-card">
            <template #header>
              <div class="card-header">
                <span>SIM卡业务状态</span>
                <el-tag type="success"
                  >正常使用: {{ statsData.activeCards || 0 }}</el-tag
                >
              </div>
            </template>
            <div class="chart-container">
              <div ref="businessStatusChart" class="chart"></div>
            </div>
            <div class="chart-legend">
              <div
                class="legend-item"
                v-for="item in businessStatusData"
                :key="item.name"
              >
                <span
                  class="legend-color"
                  :style="{ backgroundColor: item.color }"
                ></span>
                <span class="legend-text"
                  >{{ item.name }} {{ item.value }}张</span
                >
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 流量统计 -->
    <div class="traffic-stats" v-loading="loading">
      <el-row :gutter="20">
        <!-- 本月GPRS流量 -->
        <el-col :span="12">
          <el-card class="stats-card">
            <template #header>
              <div class="card-header">
                <span>本月GPRS流量</span>
                <span class="subtitle">本月每日流量</span>
              </div>
            </template>
            <div class="chart-container">
              <div ref="trafficChart" class="chart"></div>
            </div>
          </el-card>
        </el-col>

        <!-- 本月流量消耗TOP5 -->
        <el-col :span="12">
          <el-card class="stats-card">
            <template #header>
              <div class="card-header">
                <span>本月流量消耗TOP5</span>
              </div>
            </template>
            <div class="top5-table">
              <el-table :data="top5Data" stripe>
                <el-table-column prop="rank" label="序号" width="80" />
                <el-table-column prop="iccid" label="SIM卡号" />
                <el-table-column prop="usage" label="使用流量(M)" />
              </el-table>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 计费组统计 -->
    <div class="billing-group-stats" v-loading="loading">
      <el-card class="stats-card">
        <template #header>
          <div class="card-header">
            <span>计费组统计</span>
            <el-select
              v-model="selectedBillingGroup"
              placeholder="选择计费组"
              @change="handleBillingGroupChange"
              style="width: 200px"
            >
              <el-option label="全部计费组" value="all" />
              <el-option
                v-for="group in billingGroups"
                :key="group.code"
                :label="group.name"
                :value="group.code"
              />
            </el-select>
          </div>
        </template>
        <div class="billing-group-table">
          <el-table :data="billingGroupData" stripe>
            <el-table-column prop="code" label="计费组代码" width="200" />
            <el-table-column prop="name" label="计费组名称" width="200" />
            <el-table-column
              prop="trafficPoolCode"
              label="流量池代码"
              width="150"
            />
            <el-table-column
              prop="trafficPoolName"
              label="流量池名称"
              width="200"
            />
            <el-table-column prop="billingMode" label="计费模式" width="100" />
            <el-table-column
              prop="availableCards"
              label="计费组可用卡数"
              width="120"
            />
            <el-table-column
              prop="activatedCards"
              label="计费组激活卡数"
              width="120"
            />
            <el-table-column
              prop="poolAvailableCards"
              label="流量池可用卡数"
              width="120"
            />
            <el-table-column
              prop="poolActivatedCards"
              label="流量池激活卡数"
              width="120"
            />
            <el-table-column
              prop="totalTrafficMB"
              label="流量池总流量(MB)"
              width="140"
            />
            <el-table-column
              prop="usedTrafficMB"
              label="流量池已用流量(MB)"
              width="140"
            />
            <el-table-column
              prop="usedTrafficPercentage"
              label="流量池已用流量百分比"
              width="150"
            >
              <template #default="{ row }">
                {{ row.usedTrafficPercentage }}%
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-card>
    </div>

    <!-- 运营商分布 -->
    <div class="operator-stats" v-loading="loading">
      <el-card class="stats-card">
        <template #header>
          <div class="card-header">
            <span>运营商分布</span>
          </div>
        </template>
        <div class="chart-container">
          <div ref="operatorChart" class="chart"></div>
        </div>
      </el-card>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, computed } from "vue";
import { useRouter } from "vue-router";
import { ElMessage } from "element-plus";
import { Refresh, List } from "@element-plus/icons-vue";
import apiClient from "@/services/apiClient";
import { API_URLS } from "@/config";

// 路由
const router = useRouter();

// 类型定义
interface StatsData {
  totalCards: number;
  expiringCards: number;
  activeCards: number;
  functionStatus: {
    normal?: number;
    suspended?: number;
    other?: number;
  };
  businessStatus: {
    inUse?: number;
    testing?: number;
    silent?: number;
  };
  expiringStatus: {
    july?: number;
    august?: number;
    september?: number;
  };
  operatorDistribution: Record<string, number>;
  monthlyTraffic: Array<{
    date: string;
    traffic: number;
  }>;
  top5Usage: Array<{
    iccid: string;
    usage: number;
  }>;
}

// 响应式数据
const loading = ref(false);
const statsData = ref<StatsData>({
  totalCards: 0,
  expiringCards: 0,
  activeCards: 0,
  functionStatus: {},
  businessStatus: {},
  expiringStatus: {},
  operatorDistribution: {},
  monthlyTraffic: [],
  top5Usage: [],
});

// 计费组相关数据
const selectedBillingGroup = ref("all");
const billingGroups = ref<any[]>([]);
const billingGroupData = ref<any[]>([]);

// 图表引用
const functionStatusChart = ref();
const expiringChart = ref();
const businessStatusChart = ref();
const trafficChart = ref();
const operatorChart = ref();

// 图表实例
let functionStatusChartInstance: any = null;
let expiringChartInstance: any = null;
let businessStatusChartInstance: any = null;
let trafficChartInstance: any = null;
let operatorChartInstance: any = null;

// 计算属性
const functionStatusData = computed(() => {
  const data = statsData.value.functionStatus;
  return [
    { name: "正常", value: data.normal || 0, color: "#67C23A" },
    { name: "停机", value: data.suspended || 0, color: "#E6A23C" },
    { name: "其它", value: data.other || 0, color: "#409EFF" },
  ];
});

const expiringData = computed(() => {
  const data = statsData.value.expiringStatus;
  return [
    { name: "2025年07月", value: data.july || 0, color: "#67C23A" },
    { name: "2025年08月", value: data.august || 0, color: "#409EFF" },
    { name: "2025年09月", value: data.september || 0, color: "#E6A23C" },
  ];
});

const businessStatusData = computed(() => {
  const data = statsData.value.businessStatus;
  return [
    { name: "正使用", value: data.inUse || 0, color: "#67C23A" },
    { name: "测试期", value: data.testing || 0, color: "#409EFF" },
    { name: "沉默期", value: data.silent || 0, color: "#E6A23C" },
  ];
});

const top5Data = computed(() => {
  return statsData.value.top5Usage.map((item, index) => ({
    rank: index + 1,
    iccid: item.iccid,
    usage: item.usage,
  }));
});

// 方法
const fetchStatsData = async () => {
  try {
    loading.value = true;
    const response = await apiClient.get(API_URLS.SIM_CARDS_STATS);

    if (response.data.code === 0) {
      statsData.value = response.data.data;
      await nextTick();
      initCharts();
    } else {
      ElMessage.error(response.data.message || "获取统计数据失败");
    }
  } catch (error) {
    console.error("获取统计数据失败:", error);
    ElMessage.error("获取统计数据失败");
  } finally {
    loading.value = false;
  }
};

const initCharts = () => {
  // 动态导入echarts以避免SSR问题
  import("echarts").then((echarts) => {
    // 初始化功能状态图表
    if (functionStatusChart.value) {
      functionStatusChartInstance = echarts.init(functionStatusChart.value);
      const functionStatusOption = {
        tooltip: {
          trigger: "item",
          formatter: "{b}: {c} ({d}%)",
        },
        series: [
          {
            type: "pie",
            radius: ["40%", "70%"],
            data: functionStatusData.value.map((item) => ({
              name: item.name,
              value: item.value,
              itemStyle: { color: item.color },
            })),
          },
        ],
      };
      functionStatusChartInstance.setOption(functionStatusOption);
    }

    // 初始化到期图表
    if (expiringChart.value) {
      expiringChartInstance = echarts.init(expiringChart.value);
      const expiringOption = {
        tooltip: {
          trigger: "item",
          formatter: "{b}: {c} ({d}%)",
        },
        series: [
          {
            type: "pie",
            radius: ["40%", "70%"],
            data: expiringData.value.map((item) => ({
              name: item.name,
              value: item.value,
              itemStyle: { color: item.color },
            })),
          },
        ],
      };
      expiringChartInstance.setOption(expiringOption);
    }

    // 初始化业务状态图表
    if (businessStatusChart.value) {
      businessStatusChartInstance = echarts.init(businessStatusChart.value);
      const businessStatusOption = {
        tooltip: {
          trigger: "item",
          formatter: "{b}: {c} ({d}%)",
        },
        series: [
          {
            type: "pie",
            radius: ["40%", "70%"],
            data: businessStatusData.value.map((item) => ({
              name: item.name,
              value: item.value,
              itemStyle: { color: item.color },
            })),
          },
        ],
      };
      businessStatusChartInstance.setOption(businessStatusOption);
    }

    // 初始化流量图表
    if (trafficChart.value) {
      trafficChartInstance = echarts.init(trafficChart.value);
      const trafficData = statsData.value.monthlyTraffic || [];
      const trafficOption = {
        tooltip: {
          trigger: "axis",
        },
        xAxis: {
          type: "category",
          data: trafficData.map((item) => item.date),
        },
        yAxis: {
          type: "value",
          name: "流量",
        },
        series: [
          {
            type: "line",
            data: trafficData.map((item) => item.traffic),
            smooth: true,
          },
        ],
      };
      trafficChartInstance.setOption(trafficOption);
    }

    // 初始化运营商图表
    if (operatorChart.value) {
      operatorChartInstance = echarts.init(operatorChart.value);
      const operatorData = statsData.value.operatorDistribution || {};
      const operatorOption = {
        tooltip: {
          trigger: "item",
          formatter: "{b}: {c} ({d}%)",
        },
        series: [
          {
            type: "pie",
            radius: "50%",
            data: Object.entries(operatorData).map(([name, value]) => ({
              name,
              value,
            })),
          },
        ],
      };
      operatorChartInstance.setOption(operatorOption);
    }
  });
};

const refreshData = () => {
  fetchStatsData();
};

const goToSimCardList = () => {
  router.push("/simCardList");
};

// 计费组相关方法
const fetchBillingGroups = async () => {
  try {
    const response = await apiClient.get(API_URLS.SIM_CARDS_BILLING_GROUPS);

    if (response.data.code === 0) {
      billingGroups.value = response.data.data.billingGroups || [];
      billingGroupData.value = response.data.data.billingGroups || [];
    }
  } catch (error) {
    console.error("获取计费组数据失败:", error);
  }
};

const handleBillingGroupChange = (value: string) => {
  if (value === "all") {
    billingGroupData.value = billingGroups.value;
  } else {
    billingGroupData.value = billingGroups.value.filter(
      (group) => group.code === value
    );
  }
};

// 生命周期
onMounted(() => {
  fetchStatsData();
  fetchBillingGroups();

  // 监听窗口大小变化，重新调整图表
  window.addEventListener("resize", () => {
    functionStatusChartInstance?.resize();
    expiringChartInstance?.resize();
    businessStatusChartInstance?.resize();
    trafficChartInstance?.resize();
    operatorChartInstance?.resize();
  });
});
</script>

<style scoped>
.sim-card-stats {
  padding: 20px;
}

.stats-overview,
.traffic-stats,
.billing-group-stats,
.operator-stats {
  margin-bottom: 20px;
}

.stats-card {
  height: 100%;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header .subtitle {
  font-size: 12px;
  color: #909399;
}

.chart-container {
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart {
  width: 100%;
  height: 100%;
}

.chart-legend {
  margin-top: 15px;
}

.legend-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: 8px;
}

.legend-text {
  font-size: 14px;
  color: #606266;
}

.top5-table {
  max-height: 300px;
  overflow-y: auto;
}

.billing-group-table {
  max-height: 400px;
  overflow-y: auto;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .chart-container {
    height: 250px;
  }
}
</style>
